import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '@/firebase';
import { callWithTimeout } from '@/utils/callWithTimeout';
import { isInternalTraffic } from '@/utils/internalTraffic';
import { trackGtagEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { logger } from '@/utils/logger';
import { storedDiagnosticVariant, type DiagnosticVariant } from '@/utils/diagnosticVariant';
import { getLeadAttribution } from '@/utils/emailAttribution';

/**
 * Turn a Pass Plan download token into a real, VERIFIED Firebase user.
 *
 * A signed Pass Plan token is only ever handed to an address that captureLead
 * accepted, and the emailed link can only be opened from that inbox — so holding
 * one is the same proof of mailbox that Google/Apple give us. The backend
 * callable `redeemPassPlanToken` therefore find-or-creates Auth with
 * `emailVerified: true` and returns a custom token to sign in with. No
 * verification email is sent from this path: there is nothing left to verify,
 * and a second mail would just stack on the diagnostic results Pam already sent.
 *
 * ALWAYS fire-and-forget. The PDF download and the held score reveal are the
 * things the visitor traded their email for; minting an account is our business,
 * not theirs, so every failure here resolves quietly. This never throws.
 */

// Shorter than the 15s capture budget: this runs alongside the download/reveal,
// so a hung App Check must not keep a promise (or a spinner, if a caller ever
// awaits it) alive longer than the thing the visitor is actually waiting for.
// `retries: 0` — redeem creates the Auth user, so a timed-out call may have run.
const REDEEM_TIMEOUT_MS = 10000;

/**
 * Set once a redeem actually signs someone in. /onboarding reads it to tell a
 * "minted at capture" visitor (verified Auth, but NO trial yet) apart from a
 * returning user, so it can let them finish the personalize walk instead of
 * sweeping them off the page as already-authenticated.
 */
const MINTED_KEY = 'swtpa_pass_plan_minted';

/** Per-token latch: /pass-plan re-renders and capture can both reach the same token. */
const attemptedTokens = new Set<string>();

export type PassPlanRedeemOutcome = 'signed-in' | 'skipped' | 'failed';

export interface PassPlanRedeemResult {
  outcome: PassPlanRedeemOutcome;
  /** True only when this redemption is what created the account (backend `created`). */
  created: boolean;
}

export function wasMintedFromPassPlan(): boolean {
  try {
    return sessionStorage.getItem(MINTED_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearPassPlanMint(): void {
  try {
    sessionStorage.removeItem(MINTED_KEY);
  } catch {
    /* storage unavailable — the flag is an optimization, not a gate */
  }
}

export async function redeemPassPlanAuth(
  token?: string | null,
  source?: 'try_capture' | 'pass_plan_link' | 'pass_plan_page',
  /** The homepage A/B arm the visitor was shown; omit when the redeem did not come from it. */
  diagnosticVariant?: DiagnosticVariant,
): Promise<PassPlanRedeemResult> {
  if (!token) return { outcome: 'skipped', created: false };

  // Everything, including the guards, runs inside the try: callers invoke this
  // with a bare `void`, so ANY escape becomes an unhandled rejection on a page
  // whose real job (the PDF, the reveal) succeeded.
  try {
    // Already signed in: this visitor is a person, not a lead. Redeeming would at
    // best be a no-op and at worst swap them onto a different uid mid-session.
    if (auth.currentUser) return { outcome: 'skipped', created: false };
    if (attemptedTokens.has(token)) return { outcome: 'skipped', created: false };
    attemptedTokens.add(token);

    // A redeem from the emailed link after a failed in-page mint still belongs to the arm this browser saw.
    const arm = diagnosticVariant ?? storedDiagnosticVariant();
    const res = await callWithTimeout(
      () =>
        httpsCallable(functions, 'redeemPassPlanToken')({
          token,
          ...(source && { source }),
          ...(arm && { diagnosticVariant: arm }),
        }),
      { timeoutMs: REDEEM_TIMEOUT_MS, retries: 0 },
    );
    const data = res?.data as { customToken?: string; created?: boolean } | undefined;
    const customToken = data?.customToken;
    if (!customToken) {
      logger.warn('passPlanAuth', 'redeemPassPlanToken returned no customToken');
      return { outcome: 'failed', created: false };
    }
    // Re-check: a concurrent sign-in could have landed while we were in flight.
    if (auth.currentUser) return { outcome: 'skipped', created: false };

    const credential = await signInWithCustomToken(auth, customToken);
    // Only a brand-new account continues through onboarding; an existing one
    // would have its profile overwritten with onboarding defaults.
    if (data.created === true) {
      try {
        sessionStorage.setItem(MINTED_KEY, '1');
      } catch {
        /* non-fatal */
      }
    }
    if (!isInternalTraffic()) {
      // The emailed link opens a fresh session with no source; carry the stored acquisition touch.
      const touch = getLeadAttribution();
      trackGtagEvent(ANALYTICS_EVENTS.PASS_PLAN_AUTH_MINTED, {
        email_verified: credential.user.emailVerified,
        created: data.created === true,
        ...(touch && {
          utm_source: touch.utmSource,
          utm_medium: touch.utmMedium,
          utm_campaign: touch.utmCampaign,
          landing_path: touch.landingPath,
          gclid: touch.gclid,
        }),
      });
    }
    logger.info('passPlanAuth', 'Minted verified user from Pass Plan token', {
      uid: credential.user.uid,
    });
    return { outcome: 'signed-in', created: data.created === true };
  } catch (err) {
    // Expected in the wild: an expired/rotated token, an offline redeem, a
    // callable that isn't deployed yet. The visitor still gets their PDF.
    logger.warn('passPlanAuth', 'Pass Plan auth mint failed (non-fatal)', { err });
    if (!isInternalTraffic()) {
      trackGtagEvent(ANALYTICS_EVENTS.PASS_PLAN_AUTH_FAILED, {
        reason: (err as { code?: string })?.code || 'unknown',
      });
    }
    return { outcome: 'failed', created: false };
  }
}
